import { useState } from 'react';
import { Input } from '../../../components/Form/Input';
import { Button } from '../../../components/Form/Button';
import { useNavigate } from 'react-router-dom';
import { handleLoginWithEmail } from '../authActions';
import { useModalStore } from '../../../shared/store/uiStore';
import ForgotPassword from './ForgotPassword';

interface FormElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}

interface LoginFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

export const LoginForm: React.FC = () => {
  const [error, setError] = useState('');
  const { openModal, closeModal } = useModalStore((state) => state);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<LoginFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const submitter = (e.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement;

    const action = submitter?.value;

    if (action === 'login') {
      const error = await handleLoginWithEmail(email, password);

      if (error) {
        setError(error);
        return;
      }
      navigate('/v1/dashboard');
    }
  };

  const passwordModal = () => {
    openModal({
      id: 'forgot-password',
      title: 'Forgot Password',
      content: <ForgotPassword />,
      size: 'sm',
      buttons: [
        {
          label: 'Submit',
          type: 'submit',
          variant: 'primary',
          form: 'forgot-password-form',
        },
        {
          label: 'Cancel',
          type: 'button',
          variant: 'outline',
          onClick: () => {
            closeModal();
          },
        },
      ],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <p className="text-red-500 text-xs text-center mb-4">{error}</p>
      )}
      <Input
        name="email"
        label="Email"
        type="email"
        placeholder="Email"
        className="w-full"
        required
      />
      <Input
        name="password"
        label="Password"
        type="password"
        placeholder="Password"
        className="w-full"
        required
      />
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <div className="flex h-6 shrink-0 items-center">
            <div className="group grid size-4 grid-cols-1">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
              />
              <svg
                fill="none"
                viewBox="0 0 14 14"
                className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
              >
                <path
                  d="M3 8L6 11L11 3.5"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="opacity-0 group-has-checked:opacity-100"
                />
                <path
                  d="M3 7H11"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="opacity-0 group-has-indeterminate:opacity-100"
                />
              </svg>
            </div>
          </div>
          <label
            htmlFor="remember-me"
            className="block text-sm/6 text-gray-900"
          >
            Remember me
          </label>
        </div>

        <div className="text-sm/6">
          <Button type="button" variant="link" onClick={passwordModal}>
            Forgot password?
          </Button>
        </div>
      </div>
      <div className="flex space-x-4">
        <Button
          type="submit"
          variant="primary"
          name="action"
          value="login"
          className="w-full py-2 text"
        >
          <span className="text-sm">Login</span>
        </Button>
      </div>
    </form>
  );
};
